/**
 * Vehicle-map "Suggest better route" waypoints and coordinate wiring.
 * Great-circle only — not road-network optimisation.
 */

import { haversineMiles, type RouteStopForSuggestion } from './routeOptimization';
import { haversineMetres } from './vehicleDayPlaybackTrack';

export type VehicleSuggestStop = RouteStopForSuggestion & {
  postcode?: string;
  stop_number?: number;
  label: string;
  orderId?: string;
};

export type VehicleSuggestExtra = {
  orderId: string;
  latitude: number;
  longitude: number;
  postcode?: string;
  label: string;
};

type TelematicsLike = {
  latitude: number | null;
  longitude: number | null;
  postcode?: string;
  stop_number?: number;
};

function validCoord(lat: number | null | undefined, lon: number | null | undefined): boolean {
  return (
    lat != null
    && lon != null
    && Number.isFinite(lat)
    && Number.isFinite(lon)
    && lat !== 0
    && lon !== 0
  );
}

function toSuggestStop(stop: TelematicsLike, fallbackLabel: string): VehicleSuggestStop {
  return {
    latitude: stop.latitude,
    longitude: stop.longitude,
    postcode: stop.postcode,
    stop_number: stop.stop_number,
    label: (stop.postcode && String(stop.postcode).trim()) || fallbackLabel,
  };
}

function nearestStopIndex(
  lat: number,
  lon: number,
  stops: TelematicsLike[],
): number {
  let bestIndex = stops.length;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let idx = 0; idx < stops.length; idx += 1) {
    const stop = stops[idx]!;
    if (!validCoord(stop.latitude, stop.longitude)) continue;
    const distance = haversineMetres(lat, lon, stop.latitude!, stop.longitude!);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = idx;
    }
  }
  return bestIndex;
}

/**
 * Sort unmatched delivery/handoff pins the same way STOPS does:
 * playback proximity rank first, then nearest telematics stop along the day.
 */
export function sortSuggestExtrasByProximity(
  extras: VehicleSuggestExtra[],
  telematicsStops: TelematicsLike[],
  proximityOrderMap: Map<string, number> = new Map(),
): VehicleSuggestExtra[] {
  return [...extras]
    .map((extra, insertionIndex) => ({ extra, insertionIndex }))
    .sort((left, right) => {
      const leftRank = proximityOrderMap.get(left.extra.orderId);
      const rightRank = proximityOrderMap.get(right.extra.orderId);
      if (leftRank != null || rightRank != null) {
        if (leftRank == null) return 1;
        if (rightRank == null) return -1;
        if (leftRank !== rightRank) return leftRank - rightRank;
      }
      const leftPos = nearestStopIndex(left.extra.latitude, left.extra.longitude, telematicsStops);
      const rightPos = nearestStopIndex(right.extra.latitude, right.extra.longitude, telematicsStops);
      if (leftPos !== rightPos) return leftPos - rightPos;
      return left.insertionIndex - right.insertionIndex;
    })
    .map(({ extra }) => extra);
}

/**
 * Build optimiser waypoints: Start / End fixed; middle = matched telematics
 * deliveries + unmatched delivery/handoff pins (proximity-ordered).
 */
export function buildVehicleSuggestStops(params: {
  visibleStops: TelematicsLike[];
  extras?: VehicleSuggestExtra[];
  proximityOrderMap?: Map<string, number>;
}): VehicleSuggestStop[] {
  const { visibleStops, extras = [], proximityOrderMap = new Map() } = params;
  if (visibleStops.length === 0) return [];
  if (visibleStops.length === 1) {
    return [toSuggestStop(visibleStops[0]!, 'Stop')];
  }

  const start = visibleStops[0]!;
  const end = visibleStops[visibleStops.length - 1]!;
  const middleTelematics = visibleStops.slice(1, -1).map((s) =>
    toSuggestStop(s, `Stop ${s.stop_number ?? ''}`),
  );
  const orderedExtras = sortSuggestExtrasByProximity(extras, visibleStops, proximityOrderMap).map(
    (e) => ({
      latitude: e.latitude,
      longitude: e.longitude,
      postcode: e.postcode,
      label: e.label,
      orderId: e.orderId,
    }),
  );

  return [
    toSuggestStop(start, 'Start'),
    ...middleTelematics,
    ...orderedExtras,
    toSuggestStop(end, 'End'),
  ];
}

/** Crow-flies path length for `order` (defaults to array order). */
export function haversinePathMiles(
  stops: RouteStopForSuggestion[],
  order?: number[],
): number {
  const indices = order ?? stops.map((_, i) => i);
  let total = 0;
  for (let i = 0; i < indices.length - 1; i += 1) {
    const a = stops[indices[i]!];
    const b = stops[indices[i + 1]!];
    if (!a || !b || !validCoord(a.latitude, a.longitude) || !validCoord(b.latitude, b.longitude)) {
      continue;
    }
    total += haversineMiles(a.latitude!, a.longitude!, b.latitude!, b.longitude!);
  }
  return total;
}

/**
 * Lon/lat pairs for OSRM / map overlay — must use the same `stops` array
 * that `suggestRouteOrder` indexed into.
 */
export function suggestStopCoordinatesForMap(
  stops: RouteStopForSuggestion[],
  orderIndices: number[],
): [number, number][] {
  return orderIndices
    .map((idx) => {
      const s = stops[idx];
      if (!s || !validCoord(s.latitude, s.longitude)) return null;
      return [s.longitude!, s.latitude!] as [number, number];
    })
    .filter((c): c is [number, number] => c != null);
}
